import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    OnInit,
    Type,
    viewChild,
    ViewContainerRef,
} from '@angular/core';
import { IConfig, IDynamicElement } from '@sirius/common';
import { WIDGETS } from './widgets.token';

interface IWidgetConfig extends IConfig {
    type?: string;
}

/**
 * Dispatches a Storyblok `widget` block to one of the concrete widget
 * components registered in the `WIDGETS` catalogue.
 *
 * `config` is intentionally NOT forwarded to the loaded widget: every
 * widget shipped so far is self-contained. A future widget needing
 * CMS-driven data should register via a typed `WidgetEntry<C>` overload
 * whose loader is statically constrained to a component declaring a
 * matching `config` input — not a runtime probe.
 */
@Component({
    selector: 'ur-widget',
    template: '<ng-template #host></ng-template>',
    styles: ':host { display: block; width: 100%; }',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetComponent implements OnInit, IDynamicElement<IWidgetConfig> {
    private readonly _widgets = inject(WIDGETS);
    private readonly _host = viewChild.required('host', { read: ViewContainerRef });

    readonly config = input<IWidgetConfig>();

    async ngOnInit(): Promise<void> {
        const type = this.config()?.type;
        const entry = type ? this._widgets.find((e) => e.key === type) : undefined;
        if (!entry) {
            const known = this._widgets.map((e) => e.key).join(', ') || '(none)';
            console.warn(`[widget] Unknown widget type "${type ?? '(missing)'}". Registered keys: ${known}`);
            return;
        }

        let componentClass: Type<unknown>;
        try {
            componentClass = await entry.loader();
        } catch (err) {
            console.error(`[widget] Failed to lazy-load widget "${type}"`, err);
            return;
        }

        this._host().createComponent(componentClass);
    }
}
